/**
 * Render crashes caught by the route error boundaries (board uvu5): give each
 * one a short id the user can quote, keep React's component stack next to it
 * for the "Technical details" block, and send one report to the Worker
 * (`POST /client-error` → a `[client-error]` line in Workers Logs).
 *
 * Reporting is fire-and-forget (`sendBeacon`, else `fetch` with `keepalive`),
 * never awaited and never throws — a broken report must not break the error
 * page. At most `MAX_REPORTS_PER_PAGE` go out per page load, and the same
 * message on the same route is only sent once.
 */
import { CURRENT_BUILD_ID } from '@/lib/app-version'
import { recordClientEvent } from '@/lib/jam/client-events'

export const CLIENT_ERROR_PATH = '/client-error'
export const MAX_REPORTS_PER_PAGE = 5
/** Per-field caps; the Worker truncates again, this just keeps beacons small. */
const MAX_MESSAGE = 1_000
const MAX_STACK = 8_000

export interface ClientErrorReport {
  errorId: string
  name: string
  message: string
  stack: string | null
  componentStack: string | null
  route: string
  buildId: string
  projectId: string | null
}

interface Remembered {
  id: string
  componentStack: string | null
}

const remembered = new WeakMap<object, Remembered>()
/**
 * A boundary that resets (Try again, a re-run match) re-renders the view, and
 * the same bug throws a fresh error object — same name and message, same id,
 * so what the page shows always matches the one report that went out.
 */
const idsByMessage = new Map<string, string>()

function idForMessage(error: unknown): string {
  const { name, message } = describeError(error)
  const key = `${name}\n${message}`
  let id = idsByMessage.get(key)
  if (!id) {
    id = newErrorId()
    idsByMessage.set(key, id)
  }
  return id
}

function entryFor(error: unknown): Remembered {
  if (typeof error !== 'object' || error === null) {
    return { id: idForMessage(error), componentStack: null }
  }
  let entry = remembered.get(error)
  if (!entry) {
    entry = { id: idForMessage(error), componentStack: null }
    remembered.set(error, entry)
  }
  return entry
}

/** Eight base-36 characters — short enough to read out over the phone. */
export function newErrorId(random: () => number = Math.random): string {
  let id = ''
  for (let i = 0; i < 8; i++) id += Math.floor(random() * 36).toString(36)
  return id
}

/** The same id for the same error object — the page and the report agree on it. */
export function errorIdFor(error: unknown): string {
  return entryFor(error).id
}

export function componentStackFor(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  return remembered.get(error)?.componentStack ?? null
}

export function describeError(error: unknown): {
  name: string
  message: string
  stack: string | null
} {
  if (error instanceof Error) {
    return { name: error.name || 'Error', message: error.message, stack: error.stack ?? null }
  }
  return { name: 'NonError', message: String(error), stack: null }
}

/**
 * A lazily loaded chunk that is gone — typically the tab still runs the
 * previous deploy. "Try again" cannot fix that; a reload can.
 */
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = describeError(error)
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Expected a JavaScript(-or-Wasm)? module script/i.test(
      message
    )
  )
}

/** The project id from a `/$projectId/...` path, null outside a project. */
export function projectIdFromPath(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0]
  if (!first || ['auth', 'settings', 'admin'].includes(first)) return null
  return first
}

export function buildClientErrorReport(
  error: unknown,
  componentStack: string | null,
  pathname: string
): ClientErrorReport {
  const { name, message, stack } = describeError(error)
  return {
    errorId: errorIdFor(error),
    name,
    message: message.slice(0, MAX_MESSAGE),
    stack: stack?.slice(0, MAX_STACK) ?? null,
    componentStack: componentStack?.slice(0, MAX_STACK) ?? null,
    route: pathname,
    buildId: CURRENT_BUILD_ID,
    projectId: projectIdFromPath(pathname),
  }
}

let sent = 0
const sentKeys = new Set<string>()

/** Test hook: forget what this page load already reported (and the ids it handed out). */
export function resetClientErrorReports() {
  sent = 0
  sentKeys.clear()
  idsByMessage.clear()
}

function send(report: ClientErrorReport): void {
  const body = JSON.stringify(report)
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon(CLIENT_ERROR_PATH, blob)) return
    }
    void fetch(CLIENT_ERROR_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {})
  } catch {
    // Reporting is best effort.
  }
}

/**
 * The router's `defaultOnCatch`: log everything to the console (full error +
 * component stack, always), remember the component stack for the details
 * block, note it for Jam, and report it to the Worker.
 */
export function handleCaughtRenderError(
  error: unknown,
  info: { componentStack?: string | null } | undefined,
  pathname: string = typeof location !== 'undefined' ? location.pathname : ''
): ClientErrorReport {
  const entry = entryFor(error)
  const componentStack = info?.componentStack ?? null
  if (componentStack) entry.componentStack = componentStack
  const report = buildClientErrorReport(error, componentStack, pathname)

  console.error(`[route-error] ${report.errorId} on ${pathname}`, error)
  if (componentStack)
    console.error(`[route-error] ${report.errorId} component stack:${componentStack}`)

  recordClientEvent({ kind: 'error', id: report.name, code: 'render', at: Date.now() })

  const key = `${report.route}\n${report.name}\n${report.message}`
  if (sent < MAX_REPORTS_PER_PAGE && !sentKeys.has(key)) {
    sent++
    sentKeys.add(key)
    send(report)
  }
  return report
}
